// Dummy drill-down data for the report table:
//   Districts  →  Blocks (per district)  →  Schools (per block)
// Deterministic (seeded) so values are stable across renders. When your API is
// ready, replace `getBlocks(district)` and `getSchools(district, block)` with
// API calls — the shapes below are what the ReportDataTable consumes.

export interface BlockRow {
  name: string;
  schools: number;
  boys: number;
  girls: number;
  students: number;
  teachers: number;
}

export interface SchoolRow {
  name: string;
  type: string;
  boys: number;
  girls: number;
  students: number;
  teachers: number;
}

// Deterministic pseudo-random in [0,1) from a string seed.
function seeded(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const BLOCK_NAMES = [
  'Adyar',
  'Ambattur',
  'Alandur',
  'Teynampet',
  'Kodambakkam',
  'Anna Nagar',
  'Royapuram',
  'Thiru Vi Ka Nagar',
  'Tondiarpet',
  'Thiruvotriyur',
  'Perambur',
  'Sholinganallur',
  'Madhavaram',
  'Valasaravakkam',
  'Perungudi',
];

const SCHOOL_TYPES = [
  'Higher Secondary School',
  'High School',
  'Middle School',
  'Primary School',
];

const SCHOOL_PREFIXES = [
  'CHETTINAD VIDYASHRAM',
  'D.A.V School',
  'D.A.V. SCHOOL PALLIKARANAI',
  'NELLAI NADAR MHSS',
  'ST. MICHAEL\u2019S ACADEMY',
  'DAV PUBLIC SCHOOL',
  'UNITY PUBLIC SCHOOL',
  "St.John's public School",
  'Sai MHSS',
  'VIDHYA MATRIC HR.SEC.SCHOOL',
  'SAN Academy',
  'THE HINDU SR SEC SCHOOL',
];

/** Blocks for a district (dummy). */
export function getBlocks(district: string): BlockRow[] {
  const rnd = seeded(`blocks:${district}`);
  const count = 8 + Math.floor(rnd() * 6); // 8–13 blocks
  return Array.from({ length: count }, (_, i) => {
    const schools = 150 + Math.floor(rnd() * 150);
    const boys = 30000 + Math.floor(rnd() * 60000);
    const girls = 28000 + Math.floor(rnd() * 55000);
    return {
      name: BLOCK_NAMES[i % BLOCK_NAMES.length],
      schools,
      boys,
      girls,
      students: boys + girls,
      teachers: 2500 + Math.floor(rnd() * 6000),
    };
  }).sort((a, b) => b.students - a.students);
}

/** Schools for a block (dummy). */
export function getSchools(district: string, block: string): SchoolRow[] {
  const rnd = seeded(`schools:${district}:${block}`);
  const count = 10 + Math.floor(rnd() * 8); // 10–17 schools
  return Array.from({ length: count }, (_, i) => {
    const boys = 900 + Math.floor(rnd() * 3500);
    const girls = 900 + Math.floor(rnd() * 3000);
    const suffix = SCHOOL_PREFIXES[i % SCHOOL_PREFIXES.length];
    return {
      name: `${suffix}${i >= SCHOOL_PREFIXES.length ? ' ' + (i + 1) : ''}`,
      type: SCHOOL_TYPES[Math.floor(rnd() * SCHOOL_TYPES.length)],
      boys,
      girls,
      students: boys + girls,
      teachers: 60 + Math.floor(rnd() * 400),
    };
  }).sort((a, b) => b.students - a.students);
}

// ─────────────────────────────────────────────
// School-level detail (dummy, seeded) — powers the drill-down "School Details"
// view: KPI header, the four charts (Management / Gender / Social / Enrolment)
// and the info panel. Swap with an API keyed by UDISE code later.
// ─────────────────────────────────────────────

/** A labelled slice for the school detail donut/bar charts. */
export interface SchoolDetailSlice {
  label: string;
  value: number; // percentage (0–100), rounded to 2 dp
  color: string;
  count?: number; // absolute count where meaningful
}

export interface SchoolDetail {
  name: string;
  udiseCode: string;
  management: string;
  category: string;
  schoolType: string;
  level: string;
  boys: number;
  girls: number;
  transgender: number;
  students: number;
  teachers: number;
  totalStaff: number;
  nonTeaching: number;
  /** KPI tiles bundle (schools is always 1 for a single school). */
  kpi: EntityKpi;
  /** Secondary metrics (GPI, PTR, Girls Share, Avg Students/School). */
  metrics: EntityMetrics;
  /** Management-Wise donut (a single school is usually one management type). */
  managementWise: SchoolDetailSlice[];
  /** Gender split donut. */
  genderSplit: SchoolDetailSlice[];
  /** Social category donut. */
  socialCategory: SchoolDetailSlice[];
  /** Enrolment by grade (Pre-KG … XII) → boys/girls counts. */
  enrolmentByGrade: { grades: string[]; boys: number[]; girls: number[] };
}

const MANAGEMENT_TYPES = [
  'Unaided (Private) School',
  'School Education Department School',
  'Fully Aided School',
  'Corporation School',
  'Municipal School',
];

const CATEGORY_BY_TYPE: Record<string, string> = {
  'Higher Secondary School': 'Hr.Sec School (I-XII)',
  'High School': 'High School (I-X)',
  'Middle School': 'Middle School (I-VIII)',
  'Primary School': 'Primary Only (I-V)',
};

const SCHOOL_TYPE_LABELS = [
  'Un-aided',
  'Government',
  'Fully Aided',
  'Partially Aided',
  'Central Govt',
];

const GRADES = [
  'Pre-KG',
  'LKG',
  'UKG',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
];

/** Round to 2 decimals. */
function pct(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 10000) / 100 : 0;
}

/**
 * Deterministic per-school detail. `type` (from the clicked SchoolRow) is used
 * to pick a sensible category/level; `boys`/`girls` seed the totals so the
 * detail view stays consistent with the row the user clicked.
 */
export function getSchoolDetail(
  district: string,
  block: string,
  schoolName: string,
  base?: Partial<SchoolRow>
): SchoolDetail {
  const rnd = seeded(`school:${district}:${block}:${schoolName}`);

  const boys = base?.boys ?? 900 + Math.floor(rnd() * 3500);
  const girls = base?.girls ?? 900 + Math.floor(rnd() * 3000);
  const transgender = Math.floor(rnd() * 4);
  const students = boys + girls + transgender;
  const schoolType =
    base?.type ?? SCHOOL_TYPES[Math.floor(rnd() * SCHOOL_TYPES.length)];

  const teachers = base?.teachers ?? 60 + Math.floor(rnd() * 400);
  const nonTeaching = 4 + Math.floor(rnd() * 18);
  const totalStaff = teachers + nonTeaching;

  const management =
    MANAGEMENT_TYPES[Math.floor(rnd() * MANAGEMENT_TYPES.length)];
  const category = CATEGORY_BY_TYPE[schoolType] ?? 'Hr.Sec School (I-XII)';
  const level = schoolType;
  const typeLabel =
    SCHOOL_TYPE_LABELS[Math.floor(rnd() * SCHOOL_TYPE_LABELS.length)];

  // UDISE: 11-digit deterministic code.
  const udiseCode = Array.from({ length: 11 }, () =>
    Math.floor(rnd() * 10)
  ).join('');

  // Management-Wise donut — a single school is one management bucket (100%).
  const managementWise: SchoolDetailSlice[] = [
    { label: management, value: 100, color: '#f2c14e', count: 1 },
  ];

  // Gender split.
  const genderSplit: SchoolDetailSlice[] = [
    {
      label: 'Boys',
      value: pct(boys, students),
      color: '#f0803c',
      count: boys,
    },
    {
      label: 'Girls',
      value: pct(girls, students),
      color: '#26a69a',
      count: girls,
    },
    {
      label: 'Transgender',
      value: pct(transgender, students),
      color: '#7e57c2',
      count: transgender,
    },
  ];

  // Social category — deterministic split summing to the student total.
  const socialFracs = [0.52, 0.25, 0.15, 0.008, 0.06, 0.012]; // OC/BC/MBC/DNC/SC/ST
  const socialColors = [
    '#f2c14e',
    '#26a69a',
    '#8bc34a',
    '#c62828',
    '#f0803c',
    '#2f5fb0',
  ];
  const socialLabels = ['OC', 'BC', 'MBC', 'DNC', 'SC', 'ST'];
  const socialCounts = socialFracs.map((f) => Math.round(students * f));
  const socialTotal = socialCounts.reduce((a, b) => a + b, 0);
  const socialCategory: SchoolDetailSlice[] = socialLabels.map((label, i) => ({
    label,
    value: pct(socialCounts[i], socialTotal),
    color: socialColors[i],
    count: socialCounts[i],
  }));

  // Enrolment by grade — spread the students across the grades the school
  // offers (all 15 buckets, weighted higher toward upper grades).
  const gradeBoys: number[] = [];
  const gradeGirls: number[] = [];
  const weights = GRADES.map((_, i) => 0.5 + i * 0.12 + rnd() * 0.4);
  const wSum = weights.reduce((a, b) => a + b, 0);
  GRADES.forEach((_, i) => {
    gradeBoys.push(Math.round((boys * weights[i]) / wSum));
    gradeGirls.push(Math.round((girls * weights[i]) / wSum));
  });

  // typeLabel is surfaced as the "School Type" field.
  void typeLabel;

  // Secondary metrics derived from the school's counts.
  const gpi = girls > 0 && boys > 0 ? girls / boys : 0;
  const ptr = teachers > 0 ? students / teachers : 0;
  const girlsShare = students > 0 ? (girls / students) * 100 : 0;
  const metrics: EntityMetrics = {
    gpi: gpi.toFixed(2),
    ptr: `${ptr.toFixed(1)} : 1`,
    girlsShare: `${girlsShare.toFixed(1)}%`,
    avgStudents: String(students), // one school → avg == its own students
  };

  return {
    name: schoolName,
    udiseCode,
    management,
    category,
    schoolType: typeLabel,
    level,
    boys,
    girls,
    transgender,
    students,
    teachers,
    totalStaff,
    nonTeaching,
    kpi: { schools: 1, students, boys, girls, teachers },
    metrics,
    managementWise,
    genderSplit,
    socialCategory,
    enrolmentByGrade: { grades: GRADES, boys: gradeBoys, girls: gradeGirls },
  };
}

// ─────────────────────────────────────────────
// Entity (District / Block) detail — powers the drill-down overview shown
// above the next-level table: KPI stat tiles, secondary metrics and the four
// charts (Management / Gender / Social / Enrolment). Seeded + derived from the
// clicked row so it stays consistent. Swap for an API later.
// ─────────────────────────────────────────────

export interface EntityKpi {
  schools: number;
  students: number;
  boys: number;
  girls: number;
  teachers: number;
}

export interface EntityMetrics {
  gpi: string; // gender parity index, e.g. "0.97"
  ptr: string; // pupil-teacher ratio, e.g. "22.1 : 1"
  girlsShare: string; // e.g. "49.2%"
  avgStudents: string; // avg students / school, e.g. "474"
}

export interface EntityDetail {
  name: string;
  /** Level caption for the highlighted tile, e.g. "Blocks Level". */
  levelLabel: string;
  kpi: EntityKpi;
  metrics: EntityMetrics;
  managementWise: SchoolDetailSlice[];
  genderSplit: SchoolDetailSlice[];
  socialCategory: SchoolDetailSlice[];
  enrolmentByGrade: { grades: string[]; boys: number[]; girls: number[] };
}

/** Base row an entity detail can be derived from (district or block). */
export interface EntityBase {
  name: string;
  schools?: number;
  boys: number;
  girls: number;
  students: number;
  teachers: number;
}

/**
 * Build a detail bundle (KPIs, metrics, charts) for a district or block from
 * its summary row. `levelLabel` is the caption shown in the highlighted KPI
 * tile ("Blocks Level" for a district, "Schools Level" for a block).
 */
export function getEntityDetail(
  seedKey: string,
  base: EntityBase,
  levelLabel: string
): EntityDetail {
  const rnd = seeded(`entity:${seedKey}`);

  const boys = base.boys;
  const girls = base.girls;
  const students = base.students || boys + girls;
  const teachers = base.teachers;
  const schools = base.schools ?? 100 + Math.floor(rnd() * 900);

  // Secondary metrics derived from the counts.
  const gpi = girls > 0 && boys > 0 ? girls / boys : 0;
  const ptr = teachers > 0 ? students / teachers : 0;
  const girlsShare = students > 0 ? (girls / students) * 100 : 0;
  const avgStudents = schools > 0 ? Math.round(students / schools) : 0;

  const metrics: EntityMetrics = {
    gpi: gpi.toFixed(2),
    ptr: `${ptr.toFixed(1)} : 1`,
    girlsShare: `${girlsShare.toFixed(1)}%`,
    avgStudents: String(avgStudents),
  };

  // Management-Wise donut (aggregate mix across the entity's schools).
  const mgmtFracs = [0.72, 0.14, 0.09, 0.003, 0.047]; // Govt/Private/Aided/Central/Other
  const mgmtColors = ['#f0803c', '#f2c14e', '#26a69a', '#c62828', '#8bc34a'];
  const mgmtLabels = [
    'Government',
    'Private',
    'Aided',
    'Central Govt',
    'Others',
  ];
  const mgmtCounts = mgmtFracs.map((f) => Math.round(schools * f));
  const mgmtTotal = mgmtCounts.reduce((a, b) => a + b, 0) || 1;
  const managementWise: SchoolDetailSlice[] = mgmtLabels.map((label, i) => ({
    label,
    value: pct(mgmtCounts[i], mgmtTotal),
    color: mgmtColors[i],
    count: mgmtCounts[i],
  }));

  // Gender split donut.
  const genderSplit: SchoolDetailSlice[] = [
    {
      label: 'Boys',
      value: pct(boys, students),
      color: '#f0803c',
      count: boys,
    },
    {
      label: 'Girls',
      value: pct(girls, students),
      color: '#26a69a',
      count: girls,
    },
    { label: 'Transgender', value: 0, color: '#7e57c2', count: 0 },
  ];

  // Social category donut.
  const socialFracs = [0.45, 0.21, 0.19, 0.007, 0.13, 0.013]; // OC/BC/MBC/DNC/SC/ST
  const socialColors = [
    '#f2c14e',
    '#26a69a',
    '#8bc34a',
    '#c62828',
    '#f0803c',
    '#2f5fb0',
  ];
  const socialLabels = ['OC', 'BC', 'MBC', 'DNC', 'SC', 'ST'];
  const socialCounts = socialFracs.map((f) => Math.round(students * f));
  const socialTotal = socialCounts.reduce((a, b) => a + b, 0) || 1;
  const socialCategory: SchoolDetailSlice[] = socialLabels.map((label, i) => ({
    label,
    value: pct(socialCounts[i], socialTotal),
    color: socialColors[i],
    count: socialCounts[i],
  }));

  // Enrolment by grade — spread students across grades, upper grades heavier.
  const gradeBoys: number[] = [];
  const gradeGirls: number[] = [];
  const weights = GRADES.map((_, i) => 0.5 + i * 0.1 + rnd() * 0.3);
  const wSum = weights.reduce((a, b) => a + b, 0);
  GRADES.forEach((_, i) => {
    gradeBoys.push(Math.round((boys * weights[i]) / wSum));
    gradeGirls.push(Math.round((girls * weights[i]) / wSum));
  });

  return {
    name: base.name,
    levelLabel,
    kpi: { schools, students, boys, girls, teachers },
    metrics,
    managementWise,
    genderSplit,
    socialCategory,
    enrolmentByGrade: { grades: GRADES, boys: gradeBoys, girls: gradeGirls },
  };
}
